import { Body, Icon } from '@assembly-js/design-system';
import { t } from 'i18next';
import React from 'react';

import {
  EMPTY_VALUE,
  getStudioPlanTitle,
  planHasFeatureAccess,
} from './studioFeatureGating';
import {
  getStudioPlanComparison,
  StudioComparisonCell,
} from './studioPlanComparison';
import { SellableStudioPlanId, StudioBaseTiers } from './studioPlanFeatures';

interface PlanComparisonTableProps {
  planIds: SellableStudioPlanId[];
  baseTiers: StudioBaseTiers;
  highlightPlanId?: SellableStudioPlanId;
}

// Plan columns are 180px of the 774px table, the label column takes the rest.
const LABEL_COLUMN_WIDTH_MULTI = 30.23;
const LABEL_COLUMN_WIDTH_SINGLE = 58.65;

const ComparisonValue: React.FC<{
  cell: StudioComparisonCell;
  planId: SellableStudioPlanId;
}> = ({ cell, planId }) => {
  if (cell.kind === 'feature') {
    return planHasFeatureAccess(planId, cell.feature) ? (
      <Icon icon="Check" className="h-4 w-4 text-black-heading" />
    ) : (
      <Icon icon="Dash" className="h-4 w-4 text-gray-300" />
    );
  }
  return (
    // pre-line keeps the designed line break inside the build-credits value.
    <Body
      size="sm"
      tag="span"
      className="whitespace-pre-line leading-[21px] text-black-heading"
    >
      {cell.values[planId] || EMPTY_VALUE}
    </Body>
  );
};

export const PlanComparisonTable: React.FC<PlanComparisonTableProps> = ({
  planIds,
  baseTiers,
  highlightPlanId,
}) => {
  const groups = getStudioPlanComparison(baseTiers);
  const labelWidth =
    planIds.length > 1 ? LABEL_COLUMN_WIDTH_MULTI : LABEL_COLUMN_WIDTH_SINGLE;
  const planWidth = (100 - labelWidth) / planIds.length;
  const highlightClass = (planId: SellableStudioPlanId) =>
    planId === highlightPlanId ? 'bg-gray-100' : '';

  return (
    <div className="overflow-hidden rounded border border-solid border-non-hover-border">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col style={{ width: `${labelWidth}%` }} />
          {planIds.map((planId) => (
            <col key={planId} style={{ width: `${planWidth}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="border-0 border-b border-solid border-non-hover-border p-4 text-left">
              <Body
                size="lg"
                tag="span"
                className="font-medium leading-6 text-black-heading"
              >
                {t(($) => $.page_studio_plans.comparison.features_column)}
              </Body>
            </th>
            {planIds.map((planId) => (
              <th
                key={planId}
                className={`border-0 border-b border-solid border-non-hover-border p-4 text-left ${highlightClass(
                  planId,
                )}`}
              >
                <Body
                  size="lg"
                  tag="span"
                  className="font-medium leading-6 text-black-heading"
                >
                  {getStudioPlanTitle(planId)}
                </Body>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <React.Fragment key={group.title}>
              <tr>
                {/* 24px above the group title, 4px between it and its first row. */}
                <td className="px-4 pb-1 pt-6">
                  <Body
                    tag="span"
                    className="font-medium leading-[22px] text-black-heading"
                  >
                    {group.title}
                  </Body>
                </td>
                {planIds.map((planId) => (
                  <td key={planId} className={highlightClass(planId)} />
                ))}
              </tr>
              {group.rows.map((row) => (
                <tr key={row.label}>
                  <td className="border-0 border-b border-solid border-divider-card px-4 py-3 align-middle">
                    <Body
                      size="sm"
                      tag="span"
                      className="leading-[21px] text-black-heading"
                    >
                      {row.label}
                    </Body>
                  </td>
                  {planIds.map((planId) => (
                    <td
                      key={planId}
                      className={`border-0 border-b border-solid border-divider-card px-4 py-3 align-middle ${highlightClass(
                        planId,
                      )}`}
                    >
                      <ComparisonValue cell={row.cell} planId={planId} />
                    </td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};
